import { useCallback, useEffect, useMemo, useState } from 'react'

import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import { logError } from '@sequencehq/utils'

import { isDemoEnv } from 'lib/environment/environment'
import {
  NavigationV3ContextType,
  Workspace
} from 'components/NavigationV3/NavigationV3Provider.types'
import { DEMO_MODE_ORG_NAME } from 'components/NavigationV3/NavigationV3Provider.constants'
import { deleteIntermediateSessionTokenCookie } from 'lib/auth/stytch'
import { needsPrimaryAuth } from 'lib/auth/organizations'

type DiscoveredWorkspace = Workspace & { needsPrimaryAuth: boolean }

type UseFetchStytchDataForNavigationV3 = () => {
  userEmail: NavigationV3ContextType['data']['userEmail'] | undefined
  userWorkspaces: NavigationV3ContextType['data']['userWorkspaces'] | undefined
  workspaceId: NavigationV3ContextType['data']['workspaceId'] | undefined
  workspaceName: NavigationV3ContextType['data']['workspaceName'] | undefined
}

export const useFetchStytchDataForNavigationV3: UseFetchStytchDataForNavigationV3 =
  () => {
    const stytch = useStytchB2BClient()
    const { member: user } = useStytchMember()

    const [discoveredWorkspaces, setDiscoveredWorkspaces] = useState<
      DiscoveredWorkspace[] | undefined
    >()

    /**
     * userEmail
     */
    const userEmail = useMemo(() => {
      if (!user) {
        return undefined
      }

      return user.email_address
    }, [user])

    /**
     * userWorkspaces
     */
    const fetchWorkspaces = useCallback(
      async () => stytch.discovery.organizations.list(),
      [stytch]
    )

    useEffect(() => {
      const populateWorkspaces = async () => {
        if (isDemoEnv()) {
          return
        }

        const fetchedWorkspaces = await fetchWorkspaces()

        const parsedWorkspaces: DiscoveredWorkspace[] =
          fetchedWorkspaces.discovered_organizations
            .map(org => ({
              id: org.organization.organization_id,
              name: org.organization.organization_name,
              needsPrimaryAuth: needsPrimaryAuth(org)
            }))
            .sort((orgA, orgB) => {
              if (orgA.name > orgB.name) {
                return 1
              }
              if (orgA.name < orgB.name) {
                return -1
              }
              return 0
            })

        setDiscoveredWorkspaces(parsedWorkspaces)
      }

      try {
        void populateWorkspaces()
      } catch (e) {
        deleteIntermediateSessionTokenCookie()
        logError(e, { hideReportDialog: true })
      }
    }, [])

    /**
     * Workspace name
     */
    const workspaceName = useMemo(() => {
      if (isDemoEnv()) {
        return DEMO_MODE_ORG_NAME
      }

      if (
        !user ||
        typeof discoveredWorkspaces === 'undefined' ||
        (typeof discoveredWorkspaces !== 'undefined' &&
          discoveredWorkspaces.length === 0)
      ) {
        return
      }

      return (
        discoveredWorkspaces.find(
          workspace => workspace.id === user.organization_id
        )?.name ?? ''
      )
    }, [user, discoveredWorkspaces])

    /**
     * Workspace ID
     */
    const workspaceId = useMemo(() => {
      if (
        !user ||
        typeof discoveredWorkspaces === 'undefined' ||
        (typeof discoveredWorkspaces !== 'undefined' &&
          discoveredWorkspaces.length === 0)
      ) {
        return
      }

      return (
        discoveredWorkspaces.find(
          workspace => workspace.id === user.organization_id
        )?.id ?? ''
      )
    }, [user, discoveredWorkspaces])

    /**
     * A workspace that wants a fresh sign-in can't be switched into with this
     * session, so it's left out. The current workspace never wants one, so it
     * always stays listed.
     */
    const userWorkspaces = useMemo(
      () =>
        discoveredWorkspaces?.filter(workspace => !workspace.needsPrimaryAuth),
      [discoveredWorkspaces]
    )

    return {
      userEmail,
      userWorkspaces,
      workspaceId,
      workspaceName
    }
  }
